import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames/bind';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { UPDATE_INTERVAL_MS, SECONDS_IN_MINUTE, SECONDS_IN_HOUR, SECONDS_IN_DAY } from '../../consts/time';
import styles from './chatMessage.module.scss';

const classNameBuilder = classnames.bind(styles);

const ChatMessageBase = ({ message, messageId, defaultText, text }) => {
    if (message) {
        return message;
    }

    if (messageId) {
        return <FormattedMessage id={messageId} defaultMessage={defaultText} />;
    }

    if (text) {
        return text;
    }

    // Fallback to prevent empty render
    return null;
};

export const ChatMessage = ({ sender, timestamp, ...rest }) => {
    const [timeAgo, setTimeAgo] = useState('');
    const initialTimestampRef = useRef(timestamp ?? Date.now());
    const messageTimestamp = timestamp ?? initialTimestampRef.current;
    const isUserMessage = sender === 'user';
    const isAIMessage = sender === 'ai';

    const formatTimeAgo = time => {
        const now = Date.now();
        const diffInSeconds = Math.floor((now - time) / 1000);

        if (diffInSeconds < SECONDS_IN_MINUTE) {
            return 'Few seconds ago';
        } else if (diffInSeconds < SECONDS_IN_HOUR) {
            const minutes = Math.floor(diffInSeconds / SECONDS_IN_MINUTE);
            return minutes === 1 ? '1 min ago' : `${minutes} min ago`;
        } else if (diffInSeconds < SECONDS_IN_DAY) {
            const hours = Math.floor(diffInSeconds / SECONDS_IN_HOUR);
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
        } else {
            const days = Math.floor(diffInSeconds / SECONDS_IN_DAY);
            return days === 1 ? '1 day ago' : `${days} days ago`;
        }
    };

    useEffect(() => {
        const updateTimeAgo = () => {
            setTimeAgo(formatTimeAgo(messageTimestamp));
        };

        // Update immediately
        updateTimeAgo();

        // Update every minute
        const interval = setInterval(updateTimeAgo, UPDATE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [messageTimestamp]);

    return (
        <div className={classNameBuilder('container', sender)}>
            <div className={classNameBuilder('message-wrapper', sender)}>
                <p className={classNameBuilder('message', sender)}>
                    <ChatMessageBase {...rest} />
                </p>
                {isUserMessage && <div className={classNameBuilder('metadata', sender)}>Me • {timeAgo}</div>}
                {isAIMessage && <div className={classNameBuilder('metadata', sender)}>Abby • {timeAgo}</div>}
            </div>
        </div>
    );
};
